"use client";

import React, { useRef, useEffect, useState } from 'react';
import { gsap } from 'gsap';
import { Send, Mail, Phone, MapPin, Clock, CheckCircle, AlertCircle } from 'lucide-react';

interface FormData {
  name: string;
  email: string;
  company: string;
  project: string;
  budget: string;
  message: string;
}

interface FormStatus {
  type: 'idle' | 'loading' | 'success' | 'error';
  message: string;
}

const ContactPage: React.FC = () => {
  const headerRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const infoRef = useRef<HTMLDivElement>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    project: '',
    budget: '',
    message: '',
  });

  const [status, setStatus] = useState<FormStatus>({
    type: 'idle',
    message: '',
  });

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Header animation
      gsap.fromTo(
        headerRef.current,
        { opacity: 0, y: 50 },
        { opacity: 1, y: 0, duration: 0.8, ease: "power2.out" }
      );

      // Form animation
      const formElements = formRef.current?.querySelectorAll('.form-element');
      if (formElements) {
        gsap.fromTo(
          formElements,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
            stagger: 0.1,
            ease: "power2.out",
            delay: 0.3,
          }
        );
      }

      // Info section animation
      const infoElements = infoRef.current?.querySelectorAll('.info-item');
      if (infoElements) {
        gsap.fromTo(
          infoElements,
          { opacity: 0, x: -30 },
          {
            opacity: 1,
            x: 0,
            duration: 0.6,
            stagger: 0.1,
            ease: "power2.out",
            delay: 0.5,
          }
        );
      }
    });

    return () => ctx.revert();
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const label = e.target.previousElementSibling;
    if (label) {
      gsap.to(label, {
        scale: 0.9,
        y: -5,
        color: '#171717',
        duration: 0.3,
        ease: "power2.out",
      });
    }
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const label = e.target.previousElementSibling;
    if (label && !e.target.value) {
      gsap.to(label, {
        scale: 1,
        y: 0,
        color: '#737373',
        duration: 0.3,
        ease: "power2.out",
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Animate submit button
    if (submitButtonRef.current) {
      gsap.to(submitButtonRef.current, {
        scale: 0.95,
        duration: 0.1,
        yoyo: true,
        repeat: 1,
        ease: "power2.out",
      });
    }

    setStatus({ type: 'loading', message: 'Sending your message...' });

    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Simulate random success/error for demo
    const isSuccess = Math.random() > 0.2;
    
    if (isSuccess) {
      setStatus({
        type: 'success',
        message: 'Thank you! Your message has been sent successfully. I&apos;ll get back to you within 24 hours.',
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        company: '',
        project: '',
        budget: '',
        message: '',
      });

      // Animate success
      gsap.to('.form-element', {
        scale: 0.98,
        duration: 0.3,
        yoyo: true,
        repeat: 1,
        ease: "power2.out",
      });
    } else {
      setStatus({
        type: 'error',
        message: 'Sorry, there was an error sending your message. Please try again or contact me directly.',
      });
    }

    // Clear status after 5 seconds
    setTimeout(() => {
      setStatus({ type: 'idle', message: '' });
    }, 5000);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleSubmit(e as React.FormEvent);
    }
  };

  return (
    <div className="bg-background text-foreground">
      {/* Header Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div ref={headerRef} className="max-w-4xl mx-auto text-center">
            <h1 className="text-6xl md:text-8xl font-bold mb-8 leading-tight">
              Let&apos;s Create
              <br />
              <span className="text-accent">Something Amazing</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Ready to bring your vision to life? I&apos;d love to hear about your project 
              and explore how we can work together to create something extraordinary.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 border-t border-border">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-16 max-w-6xl mx-auto">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <form
                ref={formRef}
                onSubmit={handleSubmit}
                onKeyDown={handleKeyDown}
                className="space-y-8"
              >
                {/* Name & Email */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="form-element relative">
                    <label
                      htmlFor="name"
                      className="absolute left-4 top-4 text-gray-500 transition-all duration-300 pointer-events-none"
                    >
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      className="w-full px-4 pt-8 pb-4 border border-border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-300 bg-background"
                      required
                    />
                  </div>
                  
                  <div className="form-element relative">
                    <label
                      htmlFor="email"
                      className="absolute left-4 top-4 text-gray-500 transition-all duration-300 pointer-events-none"
                    >
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      className="w-full px-4 pt-8 pb-4 border border-border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-300 bg-background"
                      required
                    />
                  </div>
                </div>

                {/* Company & Project Type */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="form-element relative">
                    <label
                      htmlFor="company"
                      className="absolute left-4 top-4 text-gray-500 transition-all duration-300 pointer-events-none"
                    >
                      Company
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      className="w-full px-4 pt-8 pb-4 border border-border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-300 bg-background"
                    />
                  </div>
                  
                  <div className="form-element">
                    <label htmlFor="project" className="block text-sm font-medium mb-2 text-gray-700">
                      Project Type
                    </label>
                    <select
                      id="project"
                      name="project"
                      value={formData.project}
                      onChange={handleInputChange}
                      className="w-full px-4 py-4 border border-border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-300 bg-background"
                    >
                      <option value="">Select a project type</option>
                      <option value="website">Website Development</option>
                      <option value="webapp">Web Application</option>
                      <option value="mobile">Mobile App</option>
                      <option value="ecommerce">E-commerce</option>
                      <option value="branding">Brand Identity</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                {/* Budget */}
                <div className="form-element">
                  <label htmlFor="budget" className="block text-sm font-medium mb-2 text-gray-700">
                    Project Budget (USD)
                  </label>
                  <select
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleInputChange}
                    className="w-full px-4 py-4 border border-border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-300 bg-background"
                  >
                    <option value="">Select budget range</option>
                    <option value="5k-10k">$5,000 - $10,000</option>
                    <option value="10k-25k">$10,000 - $25,000</option>
                    <option value="25k-50k">$25,000 - $50,000</option>
                    <option value="50k+">$50,000+</option>
                  </select>
                </div>

                {/* Message */}
                <div className="form-element relative">
                  <label
                    htmlFor="message"
                    className="absolute left-4 top-4 text-gray-500 transition-all duration-300 pointer-events-none"
                  >
                    Tell me about your project *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    rows={6}
                    className="w-full px-4 pt-8 pb-4 border border-border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-300 bg-background resize-none"
                    placeholder="Describe your project goals, timeline, and any specific requirements..."
                    required
                  />
                </div>

                {/* Status Messages */}
                {status.type !== 'idle' && (
                  <div className={`form-element flex items-center gap-3 p-4 rounded-lg ${
                    status.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
                    status.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
                    'bg-blue-50 text-blue-800 border border-blue-200'
                  }`}>
                    {status.type === 'success' && <CheckCircle className="w-5 h-5" />}
                    {status.type === 'error' && <AlertCircle className="w-5 h-5" />}
                    {status.type === 'loading' && (
                      <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    )}
                    <span className="text-sm">{status.message}</span>
                  </div>
                )}

                {/* Submit Button */}
                <div className="form-element">
                  <button
                    ref={submitButtonRef}
                    type="submit"
                    disabled={status.type === 'loading'}
                    className="group w-full bg-accent text-background px-8 py-4 rounded-lg hover:bg-gray-800 focus:bg-gray-800 transition-all duration-300 font-medium flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
                  >
                    {status.type === 'loading' ? (
                      <>
                        <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        Sending Message...
                      </>
                    ) : (
                      <>
                        Send Message
                        <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                      </>
                    )}
                  </button>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Press Ctrl + Enter to submit quickly
                  </p>
                </div>
              </form>
            </div>

            {/* Contact Info */}
            <div ref={infoRef} className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold mb-6">Get In Touch</h2>
                <p className="text-gray-600 leading-relaxed mb-8">
                  I typically respond within 24 hours. For urgent inquiries, 
                  feel free to reach out directly via email or phone.
                </p>
              </div>

              <div className="space-y-6">
                <div className="info-item flex items-start gap-4">
                  <div className="bg-muted p-3 rounded-lg">
                    <Mail className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Email</h3>
                    <a
                      href="mailto:hello@portfolio.com"
                      className="text-gray-600 hover:text-accent transition-colors duration-300"
                    >
                      hello@portfolio.com
                    </a>
                  </div>
                </div>

                <div className="info-item flex items-start gap-4">
                  <div className="bg-muted p-3 rounded-lg">
                    <Phone className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Phone</h3>
                    <a
                      href="tel:+1234567890"
                      className="text-gray-600 hover:text-accent transition-colors duration-300"
                    >
                      +1 (234) 567-8900
                    </a>
                  </div>
                </div>

                <div className="info-item flex items-start gap-4">
                  <div className="bg-muted p-3 rounded-lg">
                    <MapPin className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Location</h3>
                    <p className="text-gray-600">
                      San Francisco, CA<br />
                      Available worldwide
                    </p>
                  </div>
                </div>

                <div className="info-item flex items-start gap-4">
                  <div className="bg-muted p-3 rounded-lg">
                    <Clock className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Response Time</h3>
                    <p className="text-gray-600">
                      Within 24 hours<br />
                      Monday - Friday
                    </p>
                  </div>
                </div>
              </div>

              <div className="info-item pt-8 border-t border-border">
                <h3 className="font-medium mb-4">Social Links</h3>
                <div className="flex gap-4">
                  <a
                    href="https://linkedin.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-muted p-3 rounded-lg hover:bg-gray-200 transition-colors duration-300"
                    aria-label="LinkedIn"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z"/>
                    </svg>
                  </a>
                  <a
                    href="https://github.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-muted p-3 rounded-lg hover:bg-gray-200 transition-colors duration-300"
                    aria-label="GitHub"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z" clipRule="evenodd"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;